// src/pages/MarketplacePricing/BulkPricing.jsx

import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  bulkCalculate,
  DEFAULT_MARKETPLACE_PARAMS,
  formatCurrency,
  formatPercent,
  getMarketplaceList,
} from '../../utils/pricingUtils';
import * as XLSX from 'xlsx';
import './MarketplacePricing.css';

function BulkPricing() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const fileInputRef = useRef(null);

  const [importedProducts, setImportedProducts] = useState([]);
  const [calculatedResults, setCalculatedResults] = useState([]);
  const [selectedMarketplace, setSelectedMarketplace] = useState('trendyol');
  const [exchangeRate, setExchangeRate] = useState('1');
  const [isCalculated, setIsCalculated] = useState(false);

  const [customCommission, setCustomCommission] = useState(((DEFAULT_MARKETPLACE_PARAMS.trendyol.commissionRate || 0) * 100).toString());
  const [platformServiceFee, setPlatformServiceFee] = useState((DEFAULT_MARKETPLACE_PARAMS.trendyol.fixedFee || 0).toString());
  const [productVatIncluded, setProductVatIncluded] = useState('true');
  const [productVatRate, setProductVatRate] = useState('20');
  const [shippingVatIncluded, setShippingVatIncluded] = useState('true');
  const [withholdingRate, setWithholdingRate] = useState('0');

  const marketplaces = getMarketplaceList();
  const activeMP = DEFAULT_MARKETPLACE_PARAMS[selectedMarketplace];

  // Excel dosyası yükle
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = evt.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      // Sütun eşleştirme
      const products = jsonData.map((row, index) => ({
        id: index + 1,
        name: row['Ürün Adı'] || row['Ürün'] || row['name'] || row['Name'] || `Ürün ${index + 1}`,
        code: row['Kod'] || row['code'] || row['SKU'] || '',
        rawMaterialCost: parseFloat(row['Hammadde'] || row['Maliyet'] || row['rawMaterialCost'] || row['cost'] || 0),
        laborCost: parseFloat(row['İşçilik'] || row['laborCost'] || row['labor'] || 0),
        packagingCost: parseFloat(row['Paketleme'] || row['packagingCost'] || row['packaging'] || 0),
        shippingCost: parseFloat(row['Kargo'] || row['shippingCost'] || row['shipping'] || 0),
        subcontractingCost: parseFloat(row['Fason'] || row['subcontractingCost'] || 0),
        salePrice: parseFloat(row['Satış Fiyatı'] || row['salePrice'] || row['price'] || 0),
        targetProfitMargin: parseFloat(row['Hedef Kâr'] || row['targetProfitMargin'] || row['margin'] || 0),
        returnRate: parseFloat(row['İade Oranı'] || row['returnRate'] || 0) / 100,
        exchangeRate: parseFloat(exchangeRate) || 1,
      }));

      setImportedProducts(products);
      setIsCalculated(false);
      setCalculatedResults([]);
    };
    reader.readAsBinaryString(file);
  };

  // Toplu hesaplama
  const handleBulkCalculate = () => {
    if (importedProducts.length === 0) return;

    const mp = {
      ...activeMP,
      shippingCost: activeMP.shippingCost || 0,
      commissionRate: (parseFloat(customCommission) || 0) / 100,
      fixedFee: parseFloat(platformServiceFee) || 0,
    };

    const mappedProducts = importedProducts.map(p => ({
      ...p,
      productVatIncluded: productVatIncluded === 'true',
      productVatRate: (parseFloat(productVatRate) || 20) / 100,
      shippingVatIncluded: shippingVatIncluded === 'true',
      withholdingRate: (parseFloat(withholdingRate) || 0) / 100,
      exchangeRate: parseFloat(exchangeRate) || 1,
    }));

    const results = bulkCalculate(mappedProducts, mp);
    setCalculatedResults(results);
    setIsCalculated(true);
  };

  // Excel'e dışa aktar
  const handleExport = () => {
    if (calculatedResults.length === 0) return;

    const exportData = calculatedResults.map(r => ({
      'Ürün Adı': r.productName,
      'Kod': r.code || '',
      'Hammadde (₺)': r.breakdown?.rawMaterialCost || r.rawMaterialCost || 0,
      'İşçilik (₺)': r.breakdown?.laborCost || r.laborCost || 0,
      'Paketleme (₺)': r.breakdown?.packagingCost || r.packagingCost || 0,
      'Fason (₺)': r.breakdown?.subcontractingCost || r.subcontractingCost || 0,
      'Brüt Maliyet (₺)': r.totalProductCost,
      'İndirilecek KDV (Maliyet) (₺)': r.productVat,
      'Satış Fiyatı (KDV Dahil) (₺)': r.calculatedSalePrice || r.salePrice,
      'Hesaplanan KDV (Satış KDV) (₺)': r.vatAmount,
      'KDV Hariç Fiyat (₺)': r.calculatedPriceExVat || r.priceExVat,
      'Brüt Komisyon (₺)': r.commissionAmount,
      'İndirilecek KDV (Komisyon) (₺)': r.commissionVat,
      'Brüt Kargo (₺)': r.shippingExpense,
      'İndirilecek KDV (Kargo) (₺)': r.shippingVat,
      'Brüt Reklam (₺)': r.adCost,
      'İndirilecek KDV (Reklam) (₺)': r.adVat,
      'Net KDV Ödemesi (₺)': r.netVatToPay,
      'İade Etkisi KDV Hariç (₺)': r.returnImpact,
      'Net Kâr (₺)': r.netProfit,
      'Kâr Marjı (%)': r.profitMargin,
      'Durum': r.isProfitable ? 'Kârlı' : 'Zararda',
      'Pazaryeri': activeMP.name,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);

    // Sütun genişlikleri
    ws['!cols'] = [
      { width: 25 }, { width: 12 }, { width: 14 }, { width: 12 },
      { width: 14 }, { width: 12 }, { width: 16 }, { width: 22 },
      { width: 18 }, { width: 14 }, { width: 12 }, { width: 12 },
      { width: 14 }, { width: 14 }, { width: 14 }, { width: 10 },
      { width: 14 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fiyatlandırma');
    XLSX.writeFile(wb, `pazaryeri_fiyatlama_${activeMP.name}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Şablon Excel indir
  const handleDownloadTemplate = () => {
    const templateData = [
      {
        'Ürün Adı': 'Örnek Ürün 1',
        'Kod': 'SKU001',
        'Hammadde': 100,
        'İşçilik': 20,
        'Paketleme': 10,
        'Kargo': 30,
        'Fason': 0,
        'Satış Fiyatı': 350,
        'Hedef Kâr': 0,
        'İade Oranı': 5,
      },
      {
        'Ürün Adı': 'Örnek Ürün 2',
        'Kod': 'SKU002',
        'Hammadde': 200,
        'İşçilik': 30,
        'Paketleme': 15,
        'Kargo': 40,
        'Fason': 50,
        'Satış Fiyatı': 0,
        'Hedef Kâr': 25,
        'İade Oranı': 3,
      },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    ws['!cols'] = [
      { width: 20 }, { width: 12 }, { width: 12 }, { width: 12 },
      { width: 12 }, { width: 12 }, { width: 12 }, { width: 14 },
      { width: 12 }, { width: 12 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Şablon');
    XLSX.writeFile(wb, 'fiyatlama_sablonu.xlsx');
  };

  // Özet veriler
  const summary = useMemo(() => {
    if (calculatedResults.length === 0) return null;
    const profitable = calculatedResults.filter(r => r.isProfitable);
    const totalProfit = calculatedResults.reduce((sum, r) => sum + (r.netProfit || 0), 0);
    const avgMargin = calculatedResults.reduce((sum, r) => sum + (r.profitMargin || 0), 0) / calculatedResults.length;
    return {
      total: calculatedResults.length,
      profitable: profitable.length,
      unprofitable: calculatedResults.length - profitable.length,
      totalProfit,
      avgMargin,
    };
  }, [calculatedResults]);

  return (
    <div className="mp-container">
      {/* Header */}
      <div className="mp-header">
        <div className="mp-header-left">
          <h1>📑 Toplu Fiyatlandırma</h1>
          <p>Excel ile toplu ürün fiyatlama ve kâr hesaplama</p>
        </div>
        <div className="mp-header-actions">
          <button
            className="mp-btn mp-btn-secondary"
            onClick={() => navigate('/pazaryeri-fiyatlama')}
          >
            ← Geri Dön
          </button>
        </div>
      </div>

      {/* Pazaryeri ve Kur Seçimi */}
      <div className="mp-card" style={{ marginBottom: '24px' }}>
        <div className="mp-card-body">
          <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="mp-form-group" style={{ marginBottom: 0, minWidth: '200px' }}>
              <label className="mp-form-label">Pazaryeri</label>
              <select
                className="mp-form-select"
                value={selectedMarketplace}
                onChange={(e) => {
                  const newMp = e.target.value;
                  setSelectedMarketplace(newMp);
                  setIsCalculated(false);
                  setCalculatedResults([]);
                  // Varsayılanları güncelle
                  const mpData = DEFAULT_MARKETPLACE_PARAMS[newMp];
                  if (mpData) {
                    setCustomCommission(((mpData.commissionRate || 0) * 100).toString());
                    setPlatformServiceFee((mpData.fixedFee || 0).toString());
                  }
                }}
              >
                {marketplaces.map(mp => (
                  <option key={mp.id} value={mp.id}>{mp.icon} {mp.name}</option>
                ))}
              </select>
            </div>

            {selectedMarketplace === 'amazon' && (
              <div className="mp-exchange-rate">
                <span style={{ fontSize: '20px' }}>💱</span>
                <span className="mp-exchange-label">USD/TRY Kuru:</span>
                <input
                  type="number"
                  className="mp-exchange-input"
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  step="0.01"
                />
              </div>
            )}

            <div className="mp-form-group" style={{ marginBottom: 0, minWidth: '100px' }}>
              <label className="mp-form-label">Komisyon %</label>
              <input type="number" className="mp-form-input" value={customCommission} onChange={(e) => setCustomCommission(e.target.value)} />
            </div>

            <div className="mp-form-group" style={{ marginBottom: 0, minWidth: '100px' }}>
              <label className="mp-form-label">Hizmet Bedeli ₺</label>
              <input type="number" className="mp-form-input" value={platformServiceFee} onChange={(e) => setPlatformServiceFee(e.target.value)} />
            </div>

            <div className="mp-form-group" style={{ marginBottom: 0, minWidth: '130px' }}>
              <label className="mp-form-label">Maliyetler KDV</label>
              <select className="mp-form-select" value={productVatIncluded} onChange={(e) => setProductVatIncluded(e.target.value)}>
                <option value="true">Dahil</option>
                <option value="false">Hariç</option>
              </select>
            </div>

            <div className="mp-form-group" style={{ marginBottom: 0, minWidth: '80px' }}>
              <label className="mp-form-label">Ürün KDV %</label>
              <input type="number" className="mp-form-input" value={productVatRate} onChange={(e) => setProductVatRate(e.target.value)} />
            </div>

            <div className="mp-form-group" style={{ marginBottom: 0, minWidth: '130px' }}>
              <label className="mp-form-label">Kargo KDV</label>
              <select className="mp-form-select" value={shippingVatIncluded} onChange={(e) => setShippingVatIncluded(e.target.value)}>
                <option value="true">Dahil</option>
                <option value="false">Hariç</option>
              </select>
            </div>

            <div className="mp-form-group" style={{ marginBottom: 0, minWidth: '80px' }}>
              <label className="mp-form-label">Stopaj %</label>
              <input type="number" className="mp-form-input" value={withholdingRate} onChange={(e) => setWithholdingRate(e.target.value)} />
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', paddingBottom: '2px' }}>
              <button className="mp-btn mp-btn-secondary mp-btn-sm" onClick={handleDownloadTemplate} style={{ height: '42px' }}>
                📥 Şablon İndir
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dosya Yükleme */}
      {importedProducts.length === 0 && (
        <div
          className="mp-bulk-dropzone"
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="mp-bulk-dropzone-icon">📂</div>
          <div className="mp-bulk-dropzone-title">Excel Dosyası Yükleyin</div>
          <div className="mp-bulk-dropzone-text">
            .xlsx veya .xls dosyasını buraya tıklayarak yükleyebilirsiniz.
            <br />
            Şablon dosyasını indirip kullanabilirsiniz.
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {/* Yüklenen Veriler & Sonuçlar */}
      {importedProducts.length > 0 && (
        <>
          {/* Aksiyon Butonları */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button className="mp-btn mp-btn-primary" onClick={handleBulkCalculate}>
              🧮 {isCalculated ? 'Yeniden Hesapla' : 'Toplu Hesapla'} ({importedProducts.length} ürün)
            </button>
            {isCalculated && (
              <button className="mp-btn mp-btn-success" onClick={handleExport}>
                📤 Excel'e Aktar
              </button>
            )}
            <button
              className="mp-btn mp-btn-secondary"
              onClick={() => {
                setImportedProducts([]);
                setCalculatedResults([]);
                setIsCalculated(false);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            >
              🗑️ Temizle
            </button>
          </div>

          {/* Özet */}
          {summary && (
            <div className="mp-summary-grid" style={{ marginBottom: '20px' }}>
              <div className="mp-summary-card mp-animate-in">
                <div className="mp-summary-icon">📦</div>
                <div className="mp-summary-name">Toplam Ürün</div>
                <div className="mp-summary-value">{summary.total}</div>
              </div>
              <div className="mp-summary-card mp-animate-in" style={{ borderLeft: '4px solid #059669' }}>
                <div className="mp-summary-icon">✅</div>
                <div className="mp-summary-name">Kârlı Ürün</div>
                <div className="mp-summary-value" style={{ color: '#059669' }}>{summary.profitable}</div>
              </div>
              <div className="mp-summary-card mp-animate-in" style={{ borderLeft: '4px solid #dc2626' }}>
                <div className="mp-summary-icon">❌</div>
                <div className="mp-summary-name">Zararda Ürün</div>
                <div className="mp-summary-value" style={{ color: '#dc2626' }}>{summary.unprofitable}</div>
              </div>
              <div className="mp-summary-card mp-animate-in">
                <div className="mp-summary-icon">💰</div>
                <div className="mp-summary-name">Toplam Net Kâr</div>
                <div className="mp-summary-value" style={{ color: summary.totalProfit >= 0 ? '#059669' : '#dc2626' }}>
                  {formatCurrency(summary.totalProfit)}
                </div>
              </div>
            </div>
          )}

          {/* Sonuç Tablosu */}
          <div className="mp-card">
            <div className="mp-table-container" style={{ maxHeight: '60vh' }}>
              <table className="mp-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Ürün</th>
                    <th>Maliyet</th>
                    <th>Satış Fiyatı</th>
                    {isCalculated && (
                      <>
                        <th>KDV Hariç</th>
                        <th title="Komisyon">Komisy.</th>
                        <th title="Kargo">Kargo</th>
                        <th title="Net Ödenecek KDV">Öd.KDV</th>
                        <th>Net Kâr</th>
                        <th>Marj</th>
                        <th>Durum</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(isCalculated ? calculatedResults : importedProducts).map((item, idx) => (
                    <tr key={item.id || idx}>
                      <td style={{ color: 'var(--text-muted)' }}>{idx + 1}</td>
                      <td style={{ fontWeight: '600' }}>
                        {item.productName || item.name}
                        {item.code && (
                          <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)' }}>
                            {item.code}
                          </span>
                        )}
                      </td>
                      <td>{formatCurrency(item.totalProductCost || (item.rawMaterialCost + item.laborCost + item.packagingCost + item.subcontractingCost))}</td>
                      <td>{formatCurrency(item.calculatedSalePrice || item.salePrice)}</td>
                      {isCalculated && (
                        <>
                          <td>{formatCurrency(item.calculatedPriceExVat || item.priceExVat)}</td>
                          <td style={{ color: '#d97706' }} title={`Komisyon: ${formatPercent(item.commissionRate * 100)} + ${formatCurrency(item.fixedFee)}\nToplam (KDV Dahil): ${formatCurrency(item.commissionAmount)}\nKDV: ${formatCurrency(item.commissionVat)}\nKDV'siz Gider: ${formatCurrency(item.commissionExVat)}`}>
                            {formatCurrency(item.commissionAmount)}
                          </td>
                          <td title={`Kargo: ${formatCurrency(item.shippingExpense)}\nKDV: ${formatCurrency(item.shippingVat)}\nKDV'siz Gider: ${formatCurrency(item.shippingExVat)}`}>
                            {formatCurrency(item.shippingExpense)}
                          </td>
                          <td style={{ color: item.netVatToPay > 0 ? '#dc2626' : '#059669', fontSize: '13px' }} title={`Satış KDV: ${formatCurrency(item.vatAmount)}\nİndirilecek KDV (Giderlerden): ${formatCurrency(item.totalRecoverableVat)}`}>
                            {item.netVatToPay > 0 ? '−' : '+'}{formatCurrency(Math.abs(item.netVatToPay))}
                          </td>
                          <td style={{ fontWeight: '700', color: item.netProfit > 0 ? '#059669' : item.netProfit === 0 ? '#d97706' : '#dc2626' }}>
                            {formatCurrency(item.netProfit)}
                          </td>
                          <td>
                            <span className={`mp-badge ${item.profitMargin >= 0 ? 'mp-badge-green' : 'mp-badge-red'}`}>
                              {formatPercent(item.profitMargin)}
                            </span>
                          </td>
                          <td>
                            <span className={`mp-badge ${item.netProfit > 0 ? 'mp-badge-green' : item.netProfit === 0 ? 'mp-badge-yellow' : 'mp-badge-red'}`}>
                              {item.netProfit > 0 ? '✅ Kârlı' : item.netProfit === 0 ? '⚖️ Başa-Baş' : '❌ Zarar'}
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default BulkPricing;
